import type { JourneyDay, JourneyWithDays } from "@/hooks/useJourneys";
import { getJourneyVideoSource, httpsUrl } from "@/utils/journeyMedia";
import { courseScheduleDetails, validateCourseSchedule, type CourseScheduleDetails } from "@/utils/courseSchedule";

export const COURSE_VERSION = "course_v1";
function completionImageUrl(value: unknown): string | null {
  if (
    typeof value !== "string" ||
    value.length > 2048 ||
    !/^https:\/\/\S+$/.test(value)
  ) return null;
  return httpsUrl(value) ? value : null;
}
export type CourseBlockType =
  | "text"
  | "video"
  | "powerprompt"
  | "freestyle"
  | "download";
export type CourseBlock = JourneyDay & { lesson_title?: string | null };
export interface CourseDetails extends CourseScheduleDetails {
  title: string;
  description: string;
  thumbnail_url: string;
  completion_image_url?: string;
  visibility?: "public" | "unlisted";
  access_tier: "free" | "premium";
  is_published: boolean;
  updated_at?: string;
}
export function isCourseJourney(journey: {
  experience_config?: unknown;
}): boolean {
  const config = journey.experience_config;
  return (
    !!config &&
    typeof config === "object" &&
    "version" in config &&
    config.version === COURSE_VERSION
  );
}
export function courseLessons(units: readonly CourseBlock[]) {
  const days = [...new Set(units.map((unit) => unit.day_number))].sort(
    (a, b) => a - b,
  );
  return days.map((number) => {
    const blocks = units
      .filter((unit) => unit.day_number === number)
      .sort((a, b) => a.sort_order - b.sort_order);
    return {
      number,
      title: blocks[0]?.lesson_title || `Session ${number}`,
      blocks,
    };
  });
}
export function newCourseBlock(
  type: CourseBlockType,
  day: number,
  order: number,
  lessonTitle: string,
): CourseBlock {
  return {
    id: crypto.randomUUID(),
    journey_id: "",
    day_number: day,
    sort_order: order,
    content_section: "core",
    unit_type: type,
    program_id: null,
    title: "",
    description: "",
    lesson_title: lessonTitle,
    content: "",
    audio_url: null,
    audio_duration_seconds: null,
    video_url: null,
    download_url: null,
    download_filename: null,
    duration_minutes: type === "freestyle" ? 10 : null,
  };
}
export function validateCourse(
  details: CourseDetails,
  blocks: CourseBlock[],
  publish: boolean,
): string | null {
  const scheduleIssue = validateCourseSchedule(details);
  if (scheduleIssue) return scheduleIssue;
  if (!details.title.trim()) return "Add a journey title.";
  if (details.title.length > 160)
    return "Keep the journey title under 160 characters.";
  if (
    details.visibility !== undefined &&
    details.visibility !== "public" &&
    details.visibility !== "unlisted"
  ) return "Choose who can find this journey.";
  if (details.thumbnail_url && !httpsUrl(details.thumbnail_url))
    return "Use a secure image link.";
  if (details.completion_image_url && !completionImageUrl(details.completion_image_url))
    return "Choose a valid completion image.";
  if (publish && !blocks.length) return "Add a session before publishing.";
  for (const block of blocks) {
    if (block.featured_image_url) {
      if (block.unit_type !== "text" || block.featured_image_url.length > 2048 || !httpsUrl(block.featured_image_url))
        return "Choose a valid image for a text activity.";
      if (!block.featured_image_alt?.trim() || block.featured_image_alt.trim().length > 300)
        return "Describe each image in 300 characters or fewer.";
    } else if (block.featured_image_alt?.trim()) return "Add an image or remove its description.";
    if (publish && !block.lesson_title?.trim())
      return `Give session ${block.day_number} a title.`;
    if (block.unit_type === "powerprompt" && !block.program_id)
      return "Choose a PowerPrompt for each writing block.";
    if (
      block.unit_type === "video" &&
      (publish || block.video_url) &&
      !getJourneyVideoSource(block.video_url)
    )
      return "Use a YouTube, Vimeo, Bunny, or direct video link.";
    if (
      block.unit_type === "text" &&
      publish &&
      !block.content
        ?.replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim()
    )
      return "Add text to each text block.";
    if (
      block.unit_type === "freestyle" &&
      (!Number.isInteger(block.duration_minutes) ||
        block.duration_minutes! < 1 ||
        block.duration_minutes! > 120)
    )
      return "Choose a freestyle duration from 1 to 120 minutes.";
    if (
      block.audio_url &&
      !httpsUrl(block.audio_url) &&
      !block.audio_url.startsWith("storage://journey-media/")
    )
      return "Use an uploaded audio file or a secure audio link.";
    if (
      block.unit_type === "download" &&
      (publish || block.download_url) &&
      !httpsUrl(block.download_url)
    )
      return "Add a secure resource link.";
  }
  return null;
}
export function courseDetailsFromJourney(
  journey: JourneyWithDays,
): CourseDetails {
  const config = journey.experience_config;
  const completionImage =
    config &&
    typeof config === "object" &&
    !Array.isArray(config) &&
    "completion_image_url" in config
      ? completionImageUrl(config.completion_image_url)
      : null;
  return {
    ...courseScheduleDetails(journey.experience_config),
    title: journey.title,
    description: journey.description || "",
    thumbnail_url: journey.thumbnail_url || "",
    completion_image_url: completionImage || "",
    visibility: journey.visibility === "public" ? "public" : "unlisted",
    access_tier: journey.access_tier === "free" ? "free" : "premium",
    is_published: journey.is_published,
    updated_at: journey.updated_at,
  };
}
export function nextCourseBlock(
  blocks: CourseBlock[],
  completed: ReadonlySet<string>,
) {
  return courseLessons(blocks)
    .flatMap((lesson) => lesson.blocks)
    .find((block) => !completed.has(block.id));
}

const WRITING_TYPES = new Set(["powerprompt", "freestyle"]);
export const isWritingBlock = (block: Pick<CourseBlock, "unit_type">) => WRITING_TYPES.has(block.unit_type);

/**
 * The guided session shape every Journal Party Journey uses: a Primer (text),
 * a Practice (writing), then one or more text blocks that close the day. The
 * player shows these as Primer and Practice plus a finish screen. Any other
 * mix of blocks (creator Journeys with video, downloads, several readings)
 * returns null and keeps one step per block.
 */
export function guidedSessionShape<T extends Pick<CourseBlock, "unit_type">>(blocks: readonly T[]) {
  if (blocks.length < 3) return null;
  const [primer, practice, ...after] = blocks;
  if (primer.unit_type !== "text" || !isWritingBlock(practice)) return null;
  if (!after.every((block) => block.unit_type === "text")) return null;
  return { primer, practice, wrap: after[0], extras: after.slice(1) };
}

/** Short step label for one block in a session that isn't the guided shape. */
export function courseStepLabel(block: Pick<CourseBlock, "unit_type">): string {
  switch (block.unit_type) {
    case "video": return "Watch";
    case "powerprompt":
    case "freestyle": return "Practice";
    case "download": return "Resource";
    default: return "Read";
  }
}

const BRIDGE_HEADING = /<h[2-4][^>]*>\s*where this leads\s*<\/h[2-4]>/i;

/**
 * Splits a wrap block's HTML into the takeaway and the "Where this leads"
 * bridge to the next session. Content without that heading is all takeaway.
 */
export function splitWrapContent(html: string): { takeaway: string; bridge: string | null } {
  const match = BRIDGE_HEADING.exec(html);
  if (!match) return { takeaway: html, bridge: null };
  const bridge = html.slice(match.index + match[0].length).trim();
  return { takeaway: html.slice(0, match.index).trim(), bridge: bridge || null };
}
